import { Field, FieldProps, Form, FormikErrors, FormikProps, withFormik } from 'formik'
import * as moment from 'moment'
import * as React from 'react'
import { useEffect } from 'react'
import { IOrganizationsMetadata, IRiderObject, StateOrganizationMap } from '../../../../types'
import UIMessages from '../../../../utils/ui-messages'
import { isNotBlank, isValidEmail } from '../../../../utils/validators'
import ErrorMessage from '../../../ui/ErrorMessage'
import RiderBottomBar from '../CreateRiderBottomBar'
import InnerDatePicker from './InnerDatePicker'
import { removeNotNumberCharacters } from '../../../../utils/mask'
import MaskedPhoneNumberInput from '../../../ui/formik/MaskedPhoneNumberInput'
import CharacterCounter from '../../../ui/CharacterCounter'
import PhoneInput, { isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input'
import * as classNames from 'classnames'
import { number } from 'yup'
import { Input, Textarea } from '@mobiscroll/react-latest'
import InnerRadioButton from '../../common/InnerRadioButton'
import FloatingForm from 'src/components/ui/form/FloatingForm'
import FormLabel from 'src/components/ui/form/FormLabel'

const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'

export interface IFormValues {
  formError?: string
  additionalInfo: string
  birthDate?: string
  defaultNotificationPreference: ISelectOption
  email: string
  firstName: string
  lastName: string
  notificationPreference: string
  birthday?: string
  phone?: string
  username: string
  externalId?: string
  organizations: StateOrganizationMap
  organizationsFetchingMetaData: IOrganizationsMetadata
  ownerOrganization: string
  internalNote?: string
  orcaSerialNumber?: string
  riderReferralCode?: string
}

interface ISelectOption {
  label: string
  value: string
}

interface IFormProps {
  rider: IRiderObject
  organizations: StateOrganizationMap
}

interface IRiderFormProps {
  currentStep: number
  rider: IRiderObject
  onCompleteStep: (step: number) => void
  saveRider: (rider: IRiderObject) => Promise<any>
  showMessage: (error: string, isError: boolean) => void
  setRiderState: (rider: IRiderObject) => void
  currentOrgId: string
  organizations: StateOrganizationMap
  organizationsFetchingMetaData: IOrganizationsMetadata
}

const CHARACTER_LIMIT = 200

const notificationPreferenceOptions: ISelectOption[] = [
  {
    label: UIMessages.common.textMessage,
    value: 'text'
  },
  {
    label: UIMessages.common.voice,
    value: 'voice'
  },
  {
    label: UIMessages.common.none,
    value: 'none'
  }
]

export const InnerForm = (props: IFormProps & FormikProps<IFormValues>): any => {
  const {
    touched,
    errors,
    isSubmitting,
    rider,
    organizations,
    values,
    handleBlur,
    setFieldTouched,
    setFieldValue
  } = props
  const { firstName, lastName, birthDate, orcaSerialNumber } = props.values

  const currentOrganization = organizations[props.values.ownerOrganization] || {}

  useEffect(() => {
    if (!firstName && !lastName && !birthDate && !orcaSerialNumber) {
      return
    }
    let username = ``
    username += (firstName[0] || '9').toUpperCase()
    username += (firstName[2] || '9').toUpperCase()
    username += (lastName[0] || '9').toUpperCase()
    username += (lastName[2] || '9').toUpperCase()
    let d = new Date(`${birthDate}`)
    if (isNaN(d.getTime())) {
      d = new Date()
    }

    username += `0${d.getDate()}`.slice(-2)
    username += `0${d.getMonth() + 1}`.slice(-2)
    username += `${d.getFullYear()}`.slice(-2)
    username += '1'
    username += alphabet[Math.floor(Math.random() * 26)]

    props.setFieldValue('username', username)
    props.setFieldValue('orcaSerialNumber', orcaSerialNumber)
    props.setFieldValue('riderReferralCode', generateRandomString(10))
  }, [firstName, lastName, birthDate, orcaSerialNumber])

  const textInput = ({ field }: FieldProps<IFormValues>, label: string) => {
    const textInputChange = (ev: any) => {
      field.onChange(ev)
    }
    return (
      <Input
        type='text'
        className='mobiscroll-input form mt02'
        {...field}
        onChange={textInputChange}
        label={label}
        theme='ios'
        themeVariant='light'
        inputStyle='box'
        labelStyle='floating'
      />
    )
  }

  const maskedInput = ({ field }: FieldProps<IFormValues>) => {
    return <MaskedPhoneNumberInput {...field} />
  }

  const generateRandomString = (length: number): string => {
    let result = ''
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
    const numbers = '0123456789'
    const charactersLength = characters.length
    const numbersLength = numbers.length

    for (let i = 0; i < length; i++) {
      const temp = Math.floor(Math.random() * 3)

      if (temp === 1) {
        result += numbers.charAt(Math.floor(Math.random() * numbersLength))
      } else {
        result += characters.charAt(Math.floor(Math.random() * charactersLength))
      }
    }

    return result
  }

  const textAreaInput = ({ field }: FieldProps<IFormValues>) => {
    const numOfChar = field.value ? CHARACTER_LIMIT - field.value.length : CHARACTER_LIMIT

    return (
      <div className='mt1'>
        <Textarea
          rows={4}
          cols={50}
          placeholder={UIMessages.common.riderProfileNotePlaceHolder}
          maxLength={CHARACTER_LIMIT}
          style={{ resize: 'none' }}
          className='rounded h4 mt1'
          {...field}
          theme='ios'
          themeVariant='light'
          inputStyle='box'
        />
        {touched.additionalInfo && errors.additionalInfo ? (
          <span className='font-small weight-300 font-red'>{errors.additionalInfo}</span>
        ) : (
          <CharacterCounter numOfChar={numOfChar} />
        )}
      </div>
    )
  }

  const getNotificationPreference = () => {
    const selected = notificationPreferenceOptions.find(option => option.value === props.values.notificationPreference)

    if (selected) {
      return selected
    } else {
      return notificationPreferenceOptions[2]
    }
  }

  const textAreaInternalNoteInput = ({ field }: FieldProps<IFormValues>) => {
    const numOfChar = field.value ? CHARACTER_LIMIT - field.value.length : CHARACTER_LIMIT

    return (
      <div className='mt1'>
        <Textarea
          rows={4}
          cols={50}
          placeholder={UIMessages.common.riderProfileInternalNotePlaceHolder}
          maxLength={CHARACTER_LIMIT}
          style={{ resize: 'none' }}
          className='rounded h4 mt1'
          {...field}
          theme='ios'
          themeVariant='light'
          inputStyle='box'
        />
        {touched.internalNote && errors.internalNote ? (
          <span className='font-small weight-300 font-red'>{errors.internalNote}</span>
        ) : (
          <CharacterCounter numOfChar={numOfChar} />
        )}
      </div>
    )
  }

  const handlePhoneField = (value: any) => {
    setFieldTouched('phone')
    setFieldValue('phone', value)
  }

  return (
    <Form>
      <ErrorMessage visible={!!errors.formError} className='mt1'>
        {UIMessages.common.failedRiderCreate}
      </ErrorMessage>
      <FloatingForm isSubmitting={rider.saved || isSubmitting} fullHeight={true}>
        <>
          <div className='form-modal-section main-section'>
            <div className='flex flex-row mb1'>
              <div style={{ flex: 1 }} className='mt1'>
                {/* <p>{currentOrganization.name}</p> */}
                <Input
                  type='text'
                  className='mobiscroll-input form mt02'
                  value={currentOrganization.name}
                  disabled={true}
                  theme='ios'
                  themeVariant='light'
                  inputStyle='box'
                  label={UIMessages.rider.labels.organizationDropdown}
                  labelStyle='floating'
                />
                <input type='hidden' name='ownerOrganization' />
                {touched.ownerOrganization && errors.ownerOrganization && (
                  <span className='ErrorMsgDivForOwnerOrganization font-small weight-300 font-red'>
                    {errors.ownerOrganization}
                  </span>
                )}
              </div>
            </div>

            <div className='flex flex-row mb1'>
              <div style={{ flex: 1 }} className='mt1'>
                <Field
                  className='FieldFirstName'
                  name='firstName'
                  render={field => textInput(field, `${UIMessages.common.firstName}*`)}
                />
                {touched.firstName && errors.firstName && (
                  <span className='ErrorMsgDivForFirstName font-small weight-300 font-red'>{errors.firstName}</span>
                )}
              </div>
              <div style={{ flex: 1 }} className='mt1 ml1'>
                <Field name='lastName' render={field => textInput(field, `${UIMessages.common.lastName}*`)} />
                {touched.lastName && errors.lastName && (
                  <span className='ErrorMsgDivForLastName font-small weight-300 font-red'>{errors.lastName}</span>
                )}
              </div>
            </div>
            <div className='flex flex-row full-width-datepicker mt1 mb1'>
              <div style={{ flex: 1 }} className='full-width-datepicker ios-datepicker'>
                <div className='datepicker-label'>{UIMessages.common.birthday}</div>
                <InnerDatePicker name={'birthDate'} type='long' label='Birthday' />
              </div>
              <div style={{ flex: 1 }} className='ml1'>
                <PhoneInput
                  className={classNames('phone-input', {
                    'border-red focus-red': !!(touched.phone && errors.phone)
                  })}
                  name='phoneNumber'
                  placeholder='Phone number'
                  defaultCountry='US'
                  value={values.phone}
                  onChange={handlePhoneField}
                  onBlur={handleBlur}
                />
                {touched.phone && errors.phone && (
                  <span className='font-small weight-300 font-red'>{errors.phone}</span>
                )}
              </div>
            </div>

            <div className='flex flex-row mb1'>
              <div style={{ flex: 1 }} className='mt1'>
                <Field name='username' render={field => textInput(field, `${UIMessages.common.username}*`)} />
                {touched.username && errors.username && (
                  <span className='font-small weight-300 font-red'>{errors.username}</span>
                )}
              </div>
              <div style={{ flex: 1 }} className='block ml1 mt1'>
                <Field name='email' render={field => textInput(field, `${UIMessages.common.email}`)} />
                {touched.email && errors.email && (
                  <span className='ErrorMsgDivForEmail font-small weight-300 font-red'>{errors.email}</span>
                )}
              </div>
            </div>

            {/* Add Orca Serial Number */}
            <div className='flex flex-row mb1'>
              <div style={{ flex: 1 }} className='block mt1'>
                <Field name='orcaSerialNumber' render={field => textInput(field, `${UIMessages.common.orca}`)} />
                {touched.orcaSerialNumber && errors.orcaSerialNumber && (
                  <span className='ErrorMsgDivForEmail font-small weight-300 font-red'>{errors.orcaSerialNumber}</span>
                )}
              </div>
              <div style={{ flex: 1 }} className='block ml1 mt1'>
                <Field
                  name='riderReferralCode'
                  render={field => textInput(field, `${UIMessages.common.riderReferralCode}`)}
                />
                {touched.riderReferralCode && errors.riderReferralCode && (
                  <span className='ErrorMsgDivForEmail font-small weight-300 font-red'>{errors.riderReferralCode}</span>
                )}
              </div>
            </div>

            <div className='flex flex-row'>
              <div style={{ flex: 1 }} className='mt1'>
                <FormLabel>{UIMessages.common.riderProfileNote}</FormLabel>
                <Field name='additionalInfo' render={textAreaInput} />
              </div>
            </div>

            <div className='flex flex-row'>
              <div style={{ flex: 1 }} className='mt1'>
                <FormLabel>{UIMessages.common.riderProfileInternalNote}</FormLabel>
                <Field name='internalNote' render={textAreaInternalNoteInput} />
              </div>
            </div>
          </div>
          <div className='form-modal-section sub-section'>
            <FormLabel>Mobile Settings</FormLabel>

            <div className='bb-shadow-grey'>&nbsp;</div>

            <div className='flex'>
              <div className='mt1'>
                <div className='pr1 pb1 pt1'>
                  <FormLabel>{UIMessages.common.notificationSetting}</FormLabel>
                  <InnerRadioButton
                    default={getNotificationPreference()}
                    options={notificationPreferenceOptions}
                    name={'notificationPreference'}
                  />
                </div>
              </div>
            </div>

            <div className='bb-shadow-grey'>&nbsp;</div>

            <div className='flex'>
              <div className='mt1 w100'>
                <div className='pr1 pb1 pt1'>
                  <Field name='externalId' render={field => textInput(field, `Profile External ID`)} />
                  {touched.externalId && errors.externalId && (
                    <span className='font-small weight-300 font-red'>{errors.externalId}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      </FloatingForm>
      <RiderBottomBar isSaved={rider.saved} isSaving={isSubmitting} nextStepPath={'/riders/create/location'} />
    </Form>
  )
}

const validateName = (name: string) => {
  let isValidName = true
  if (/[!@#$%^&*(),.?":{}|<>]/g.test(name) || !/^[A-Z]/.test(name)) {
    isValidName = false
  }
  return isValidName
}

export const PersonalInfoForm = withFormik<IRiderFormProps, IFormValues>({
  mapPropsToValues: (props: IRiderFormProps): IFormValues => {
    const {
      additionalInfo,
      internalNote,
      birthDate,
      email,
      firstName,
      lastName,
      notificationPreference,
      externalId,
      phone,
      username,
      ownerOrganization,
      orcaSerialNumber,
      riderReferralCode
    } = props.rider
    return {
      additionalInfo,
      internalNote,
      email,
      firstName,
      lastName,
      notificationPreference,
      externalId,
      username,
      orcaSerialNumber: orcaSerialNumber || undefined,
      riderReferralCode,
      ownerOrganization: props.currentOrgId,
      organizations: props.organizations,
      organizationsFetchingMetaData: props.organizationsFetchingMetaData,
      birthday: birthDate || undefined,
      birthDate: birthDate || undefined,
      phone: phone ? phone : '',
      defaultNotificationPreference:
        Object.values(notificationPreferenceOptions).find((it: any) => it.value === notificationPreference) ||
        notificationPreferenceOptions[0] // Default to none if undefined
    }
  },
  validate: (values: IFormValues, { showMessage }): FormikErrors<IFormValues> => {
    const { email, firstName, lastName, phone, ownerOrganization, username, orcaSerialNumber } = values
    const phoneNumber = phone ? phone : ''

    const errors: FormikErrors<IFormValues> = {}
    if (!isNotBlank(firstName)) {
      errors.firstName = UIMessages.rider.validation.firstNameIsRequired
      showMessage(UIMessages.rider.validation.fillAllRequiredFields, true)
    } else if (!validateName(firstName)) {
      errors.firstName = UIMessages.rider.validation.firstNameIsInvalid
      showMessage(UIMessages.rider.validation.invalidName, true)
    }

    if (!isNotBlank(lastName)) {
      errors.lastName = UIMessages.rider.validation.lastNameIsRequired
      showMessage(UIMessages.rider.validation.fillAllRequiredFields, true)
    } else if (!validateName(lastName)) {
      errors.lastName = UIMessages.rider.validation.lastNameIsInvalid
      showMessage(UIMessages.rider.validation.invalidName, true)
    }

    if (!isNotBlank(username)) {
      errors.username = UIMessages.rider.validation.usernameIsRequired
      showMessage(UIMessages.rider.validation.fillAllRequiredFields, true)
    }

    if (email && !isValidEmail(email)) {
      errors.email = UIMessages.rider.validation.invalidEmailAddress
      showMessage(UIMessages.rider.validation.invalidEmailAddress, true)
    }
    if (!isNotBlank(ownerOrganization) || ownerOrganization === UIMessages.rider.defaultValues.organization.value) {
      errors.ownerOrganization = UIMessages.rider.validation.organizationOwnerIsRequired
      showMessage(UIMessages.rider.validation.fillAllRequiredFields, true)
    }
    if (phoneNumber !== '' && !isValidPhoneNumber(phoneNumber)) {
      errors.phone = UIMessages.rider.validation.phoneNumberInvalid
    }

    return errors
  },
  handleSubmit: async (
    {
      additionalInfo,
      internalNote,
      birthDate,
      email,
      firstName,
      lastName,
      notificationPreference,
      phone,
      username,
      orcaSerialNumber,
      riderReferralCode,
      externalId,
      ownerOrganization
    }: IFormValues,
    { props, setSubmitting }
  ) => {
    setSubmitting(true)
    const phoneInfo = parsePhoneNumber(phone ? phone : '')
    const data = {
      ...props.rider,
      additionalInfo,
      internalNote,
      email,
      firstName,
      lastName,
      notificationPreference,
      username,
      orcaSerialNumber,
      riderReferralCode,
      externalId,
      ownerOrganization,
      birthDate: moment(birthDate).format('MMM DD YYYY'),
      phone: phoneInfo ? phoneInfo.nationalNumber : '',
      countryCode: phoneInfo ? phoneInfo.countryCallingCode : '',
      saved: true
    }
    await props.saveRider(data)
    props.setRiderState(data)
    setSubmitting(false)
  }
})(InnerForm)

export default PersonalInfoForm



// WEBPACK FOOTER //
// ./src/components/riders/createrider/personalinfo/PersonalInfoForm.tsx